import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RotateCw, Clock, Search } from 'lucide-react';
import { API_BASE } from '../../../config/api';

const API = `${API_BASE}/api`;
const FILTERS = [['', 'All'], ['failed', 'Failed'], ['sent', 'Sent'], ['scheduled', 'Scheduled'], ['queued,sending', 'Sending'], ['unknown', 'Unconfirmed']];
const TONE = { sent: 'text-emerald-300', failed: 'text-rose-300', scheduled: 'text-sky-300', queued: 'text-zinc-300', sending: 'text-zinc-300', unknown: 'text-amber-300' };
const SOURCE = { compose: 'Email', announcement: 'Announcement', invite: 'Sign-in link', alert: 'Alert' };
const fmt = (v) => (v ? new Date(v).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

export default function EmailMessageLog({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ messages: [], total: 0, counts: {} });
  const [sel, setSel] = useState([]);
  const [later, setLater] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/email/messages`, { headers, params: { status: status || undefined, q: q || undefined, limit: 200 } })
      .then((r) => setData(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, q, token]);
  useEffect(() => { const t = setTimeout(load, 250); const i = setInterval(load, 10000); return () => { clearTimeout(t); clearInterval(i); }; }, [load]);

  const resendable = data.messages.filter((m) => m.resendable !== false && !['queued', 'sending'].includes(m.status));
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const resend = async (when) => {
    if (!sel.length) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/email/messages/resend`, { message_ids: sel, send_at: when ? new Date(when).toISOString() : null }, { headers });
      toast.success(r.data.scheduled_for ? `${r.data.count} email(s) scheduled for ${fmt(r.data.scheduled_for)}` : `Resending ${r.data.count} email(s) now`);
      setSel([]); setLater(''); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resend');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden" data-testid="email-message-log">
      <div className="px-5 py-4 border-b border-zinc-800 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-white font-semibold">Email log</h2>
            <p className="text-xs text-zinc-500">Every email the platform has sent, including earlier ones. Tick emails to resend them now or later.</p>
          </div>
          <div className="relative">
            <Search className="h-4 w-4 text-zinc-500 absolute left-2.5 top-2.5" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipient or subject" className="h-9 w-64 pl-8 pr-2 rounded-md bg-navigate-dark border border-zinc-700 text-sm text-white" data-testid="email-log-search" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([v, l]) => {
            const n = v ? v.split(',').reduce((a, k) => a + (data.counts?.[k] || 0), 0) : null;
            return (
              <button key={l} onClick={() => { setStatus(v); setSel([]); }} data-testid={`email-log-filter-${l.toLowerCase()}`}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${status === v ? 'border-navigate-gold text-navigate-gold bg-navigate-gold/10' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}>
                {l}{n !== null ? ` (${n})` : ''}
              </button>
            );
          })}
        </div>
        {sel.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-zinc-900/60 border border-zinc-800 p-2" data-testid="email-resend-bar">
            <span className="text-xs text-zinc-300 mr-1">{sel.length} selected</span>
            <button onClick={() => resend(null)} disabled={busy} className="btn-gold px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid="email-resend-now-btn"><RotateCw className="h-3.5 w-3.5" />Resend now</button>
            <input type="datetime-local" value={later} onChange={(e) => setLater(e.target.value)} className="h-8 rounded-md bg-navigate-dark border border-zinc-700 text-xs text-white px-2" data-testid="email-resend-later-input" />
            <button onClick={() => resend(later)} disabled={busy || !later} className="px-3 py-1.5 text-xs rounded-full border border-zinc-600 text-zinc-200 hover:border-navigate-gold disabled:opacity-50 flex items-center gap-1.5" data-testid="email-resend-later-btn"><Clock className="h-3.5 w-3.5" />Resend later</button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 sticky top-0">
            <tr>
              <th className="px-4 py-3 w-8"><input type="checkbox" checked={resendable.length > 0 && resendable.every((m) => sel.includes(m.message_id))}
                onChange={(e) => setSel(e.target.checked ? resendable.map((m) => m.message_id) : [])} data-testid="email-log-select-all" /></th>
              {['Recipient', 'Subject', 'Type', 'Status', 'Date'].map((h) => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {data.messages.length === 0 ? <tr><td colSpan={6} className="px-5 py-10 text-center text-zinc-500" data-testid="email-messages-empty">No emails here.</td></tr>
              : data.messages.map((m) => {
                const can = m.resendable !== false && !['queued', 'sending'].includes(m.status);
                return (
                  <tr key={m.message_id} data-testid={`email-message-${m.message_id}`}>
                    <td className="px-4 py-3"><input type="checkbox" disabled={!can} checked={sel.includes(m.message_id)} onChange={() => toggle(m.message_id)} title={can ? '' : 'This older email has no saved content to resend'} data-testid={`email-select-${m.message_id}`} /></td>
                    <td className="px-4 py-3 text-zinc-200 max-w-[220px] truncate" title={m.to}>{m.name ? <>{m.name} <span className="text-zinc-500">· {m.to}</span></> : m.to}</td>
                    <td className="px-4 py-3 text-zinc-300 max-w-[240px] truncate" title={m.subject}>{m.subject}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{SOURCE[m.source] || m.source}{m.historical ? ' · earlier' : ''}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium capitalize ${TONE[m.status] || 'text-zinc-300'}`}>{m.status === 'unknown' ? 'Unconfirmed' : m.status}{m.attempts > 1 ? ` · ${m.attempts} tries` : ''}</span>
                      {m.status === 'scheduled' && <p className="text-[11px] text-sky-300/80">for {fmt(m.scheduled_for)}</p>}
                      {m.last_error && !['scheduled', 'queued', 'sending'].includes(m.status) && <p className="text-[11px] text-rose-300/80 max-w-xs truncate" title={m.last_error}>{m.last_error}</p>}
                    </td>
                    <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">{fmt(m.sent_at || m.created_at)}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      {data.total > data.messages.length && <p className="px-5 py-3 text-xs text-zinc-500 border-t border-zinc-800">Showing the latest {data.messages.length} of {data.total}. Search or filter to find older emails.</p>}
    </div>
  );
}
